// ===== Packages =====

import React, {
    useState,
    useEffect,
}                                               from 'react';
import styled                                   from 'styled-components';
import {
    useFocused,
    useSelected,
}                                               from 'slate-react';
import {
    TwitterTweetEmbed,
    TwitterTimelineEmbed,
}                                               from 'react-twitter-embed';

// ===== Services =====

import {
    getTouchMouseEventHandlers,
    getTwitterId,
    recordUserAction,
    setColorLightness,
    getPressEventHandlers,
    isTestMode,
}                                               from '../../../services';

// ===== Interfaces =====

import {
    ITwitterData,
    IPartialUserItem,
}                                               from '../../../interfaces';
import { instanceOfTwitterData }                from '../../../interfaces/ITwitterData';

// ===== Enums =====

import {
    USER_ACTION_TYPE,
    EDITOR_CONTEXT_TYPE,
    CURSOR_TARGET,
}                                               from '../../../enums';

// ===== Constants =====

import {
    BODY_FONT_SIZE,
    EDITOR_SELECTION_LIGHTNESS_VALUE,
    EDITOR_SELECTION_BORDER_THICKNESS,
    HOVER_TARGET_CLASSNAME,
}                                               from '../../../constants/generalConstants';
import CURSOR_SIGN                              from '../../../constants/cursorSigns';

// Example Tweet: https://twitter.com/afikanyati/status/1172996040192069633
// Example Timeline: https://twitter.com/EricRWeinstein

TwitterEmbed.defaultProps = {
    onCursorEnter: undefined,
    onCursorLeave: undefined,
    onLoad: undefined,
};
interface Props {
    id: string,
    url: string,
    color: string,
    user: IPartialUserItem | null,
    currentSessionId: string | null,
    postId: string | undefined,
    editorType: EDITOR_CONTEXT_TYPE,
    readOnly: boolean,
    children: React.ReactElement,
    attributes: any,
    onCursorEnter?: (
        targetType: CURSOR_TARGET | string,
        actions: string[],
        target?: HTMLElement,
    ) => void,
    onCursorLeave?: (e?: React.MouseEvent | React.TouchEvent | React.SyntheticEvent) => void,
    onLoad?: () => void,
}
function TwitterEmbed({
    id,
    url,
    color,
    user,
    currentSessionId,
    postId,
    editorType,
    readOnly,
    children,
    attributes,
    onCursorEnter,
    onCursorLeave,
    onLoad,
}: Props): JSX.Element {
    // ===== State =====

    const [tweetId, setTweetId] = useState<string>('');
    const [twitterUsername, setTwitterUsername] = useState<string>('');
    const focused = useFocused();
    const selected = useSelected();

    // ===== Side Effects =====

    useEffect(() => {
        const data = getTwitterId(url);

        if (instanceOfTwitterData(data)) {
            const { id: twtId, username } = data as ITwitterData;
            if (twtId) {
                setTweetId(twtId);
            }

            if (username) {
                setTwitterUsername(username);
            }
        }
    }, [url]);

    const clickTweet = async (): Promise<void> => {
        if (
            readOnly
            && postId
            && currentSessionId
        ) {
            // Record user action
            recordUserAction({
                test: isTestMode(),
                type: USER_ACTION_TYPE.clickTweet,
                sessionId: currentSessionId,
                payload: {
                    id,
                    postId,
                    editorType,
                },
            });
        }
    };

    const onMouseEnter = (e: React.MouseEvent): void => {
        if (onCursorEnter) {
            onCursorEnter(
                CURSOR_TARGET.editorElement,
                [CURSOR_SIGN.click],
                e.currentTarget as HTMLElement,
            );
        }
    };

    const onMouseLeave = (e: React.MouseEvent): void => {
        if (onCursorLeave) onCursorLeave(e);
    };

    return (
        <Container
            {...attributes}
            className={HOVER_TARGET_CLASSNAME}
            color={color}
            focused={focused}
            selected={selected}
            isTimeline={tweetId.length === 0
            && twitterUsername.length > 0}
            {...getTouchMouseEventHandlers({
                onMouseEnter,
                onMouseLeave,
            })}
            {...getPressEventHandlers({ onMouseDown: clickTweet })}
        >
            {tweetId.length > 0
            && (
                <EmbedContainer
                    // Necessary to avoid: Cannot resolve a Slate point from DOM point
                    // Reference: https://github.com/ianstormtaylor/slate/issues/3930
                    contentEditable={false}
                >
                    <TwitterTweetEmbed
                        tweetId={tweetId}
                        onLoad={onLoad}
                    />
                </EmbedContainer>
            )}
            {tweetId.length === 0
            && twitterUsername.length > 0
            && (
                <EmbedContainer
                    // Necessary to avoid: Cannot resolve a Slate point from DOM point
                    // Reference: https://github.com/ianstormtaylor/slate/issues/3930
                    contentEditable={false}
                >
                    <TwitterTimelineEmbed
                        sourceType="profile"
                        screenName={twitterUsername}
                        options={{
                            height: 0.7 * window.innerHeight,
                        }}
                        onLoad={onLoad}
                    />
                </EmbedContainer>
            )}
            {children}
        </Container>
    );
}

// ===== Styled Components =====

interface ContainerProps {
    focused: boolean,
    selected: boolean,
    color: string,
    isTimeline: boolean,
}
const Container = styled.div<ContainerProps>`
    border-radius: ${`${0.3125 * BODY_FONT_SIZE}px`};
    margin: ${`${1.25 * BODY_FONT_SIZE}px 0px`};

    & > div > * {
        display: flex;
        justify-content: center;
        border-radius: ${`${0.3125 * BODY_FONT_SIZE}px`};
        ${({ isTimeline }) => isTimeline && `
            display: flex !important;
            max-width: 600px !important;
            justify-content: center !important;
        `};
    border: ${({
        focused,
        selected,
        color,
        theme,
    }) => (focused && selected
        ? `${EDITOR_SELECTION_BORDER_THICKNESS}px solid ${color
            ? setColorLightness(
                color,
                EDITOR_SELECTION_LIGHTNESS_VALUE,
            )
            : theme.verascopeColor.purple200
        } !important`
        : 'none'
    )};
    }
`;

const EmbedContainer = styled.div``;

export default TwitterEmbed;
